import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Calendar } from "lucide-react";
import { Section, TeamLogo, StatusBadge } from "../components/UIBits";
import { useT, formatDayLocalized } from "../lib/i18n";
import { phaseLabel } from "../lib/constants";
import api from "../lib/api";

export default function TeamDetailPage() {
    const { t, lang } = useT();
    const { id } = useParams();
    const [team, setTeam] = useState(null);
    const [players, setPlayers] = useState([]);
    const [games, setGames] = useState([]);
    const [allTeams, setAllTeams] = useState([]);

    useEffect(() => {
        api.get(`/teams/${id}`).then(r => setTeam(r.data)).catch(() => {});
        api.get(`/players?team_id=${id}`).then(r => setPlayers(r.data));
        api.get(`/games`).then(r => setGames(r.data));
        api.get(`/teams?light=true`).then(r => setAllTeams(r.data));
    }, [id]);

    const teamMap = Object.fromEntries(allTeams.map(t => [t.id, t]));
    const teamGames = games.filter(g => g.home_team_id === id || g.away_team_id === id)
        .sort((a, b) => (a.day + a.time).localeCompare(b.day + b.time));

    if (!team) return <Section><div className="text-white/50">{t("common.loading")}</div></Section>;

    return (
        <div data-testid="team-detail">
            <Helmet><title>{`${team.name} — ${t("teams.roster")}`}</title></Helmet>
            <Section>
                <Link to="/equipes" className="inline-flex items-center gap-2 text-[#4FE0D5] mb-8 hover:text-[#7FD93C]"><ArrowLeft size={16} /> {t("teams.all_teams")}</Link>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mb-10 pb-8 border-b border-white/10">
                    <TeamLogo team={team} size={96} />
                    <div className="min-w-0 flex-1">
                        <div className="label-mini mb-1">{team.category}</div>
                        <h1 className="font-display text-3xl sm:text-5xl uppercase tracking-tight break-words" style={{ color: team.color || "#7FD93C" }}>{team.name}</h1>
                        {team.motto && <div className="text-white/60 italic mt-2">« {team.motto} »</div>}
                    </div>
                </div>

                {/* Team games */}
                {teamGames.length > 0 && (
                    <div className="mb-12">
                        <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-widest mb-6 flex items-center gap-3">
                            <Calendar size={22} className="text-[#4FE0D5]" />
                            {t("team.schedule") || "Calendrier"} <span className="text-white/30 text-base">({teamGames.length})</span>
                        </h2>
                        <div className="grid gap-2" data-testid="team-games">
                            {teamGames.map(g => {
                                const isHome = g.home_team_id === id;
                                const opp = teamMap[isHome ? g.away_team_id : g.home_team_id];
                                const myScore = isHome ? g.home_score : g.away_score;
                                const oppScore = isHome ? g.away_score : g.home_score;
                                const result = g.status === "final"
                                    ? (myScore > oppScore ? "V" : myScore < oppScore ? "D" : "N")
                                    : null;
                                return (
                                    <Link key={g.game_num} to={`/match/${g.game_num}`} className={`glass-card p-3 flex items-center gap-3 hover:border-[#7FD93C] transition ${g.status === "live" ? "border-l-4 border-[#7FD93C]" : ""}`} data-testid={`team-game-${g.game_num}`}>
                                        <div className="text-xs text-white/60 font-mono w-20 sm:w-28 shrink-0 truncate">{formatDayLocalized(g.day, lang)}<br />{g.time} • {g.ice}</div>
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <span className="text-[10px] text-white/40 uppercase">{isHome ? "vs" : "@"}</span>
                                            <TeamLogo team={opp} size={24} />
                                            <span className="truncate text-sm font-semibold">{opp?.name || (isHome ? g.away_placeholder : g.home_placeholder) || "—"}</span>
                                        </div>
                                        <div className="text-right shrink-0">
                                            {g.status === "final" ? (
                                                <div className="flex items-center gap-2">
                                                    <span className={`font-display text-lg ${result === "V" ? "text-[#7FD93C]" : result === "D" ? "text-rose-400" : "text-white/60"}`}>{myScore}–{oppScore}</span>
                                                    <span className={`text-xs font-bold ${result === "V" ? "text-[#7FD93C]" : result === "D" ? "text-rose-400" : "text-white/60"}`}>{result}</span>
                                                </div>
                                            ) : (
                                                <StatusBadge status={g.status} />
                                            )}
                                            {g.phase && g.phase !== "preliminaire" && (
                                                <div className="text-[9px] text-[#4FE0D5] uppercase tracking-widest mt-0.5">{phaseLabel(g.phase, lang)}</div>
                                            )}
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                )}

                <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-widest mb-6">{t("teams.roster")}</h2>
                {players.length === 0 ? (
                    <div className="text-white/50">{t("teams.no_roster")}</div>
                ) : (
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {players.map(p => (
                            <div key={p.id} className="glass-card p-4 flex items-center gap-3" data-testid={`player-${p.id}`}>
                                <div className="font-display text-3xl text-[#7FD93C] w-12">#{p.number}</div>
                                <div>
                                    <div className="font-semibold">{p.name}</div>
                                    {p.position && <div className="text-xs text-white/50">{p.position}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Section>
        </div>
    );
}
